import { useEffect, useRef, useState } from 'react';
import { CircleCheck, CircleAlert, Loader2, Wand2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * Real-time slug availability indicator.
 *
 * props:
 *   value          \u2014 current slug text (controlled)
 *   onChange(text) \u2014 user typed a new value
 *   onApplySuggestion(suggestion) \u2014 user clicked the suggestion button
 *   nameFallback   \u2014 free-text name to auto-derive from when value is empty
 *   checker(value, excludeId) \u2192 Promise<{normalized, available, suggestion, reason}>
 *   excludeId      \u2014 id of the entity being edited (so it doesn\u2019t clash with itself)
 *   onStatusChange(status: 'available'|'taken'|'empty'|'checking') optional
 *   testidPrefix
 */
export const SlugChecker = ({
  value,
  onChange,
  onApplySuggestion,
  nameFallback = '',
  checker,
  excludeId,
  onStatusChange,
  testidPrefix = 'slug',
  label = 'Slug',
  placeholder = 'auto from name',
}) => {
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  // Keep latest checker in a ref so it doesn't retrigger the debounced effect on every render.
  const checkerRef = useRef(checker);
  useEffect(() => { checkerRef.current = checker; }, [checker]);

  useEffect(() => {
    const raw = (value || '').trim() || nameFallback || '';
    if (!raw) {
      setStatus('empty');
      setResult({ available: false, normalized: '', suggestion: '', reason: 'empty' });
      onStatusChange?.('empty');
      return;
    }
    let cancelled = false;
    setStatus('checking');
    onStatusChange?.('checking');
    const timer = setTimeout(async () => {
      try {
        const r = await checkerRef.current(raw, excludeId);
        if (cancelled) return;
        setResult(r);
        const s = r.reason === 'available' ? 'available' : r.reason === 'taken' ? 'taken' : 'empty';
        setStatus(s);
        onStatusChange?.(s);
      } catch (_) {
        if (cancelled) return;
        setStatus('idle');
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, nameFallback, excludeId]);

  const norm = result?.normalized || '';
  const suggestion = result?.suggestion || '';

  const ICON = {
    available: <CircleCheck className="h-3.5 w-3.5 text-emerald-500" />,
    taken: <CircleAlert className="h-3.5 w-3.5 text-rose-500" />,
    checking: <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />,
    idle: null,
    empty: null,
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        <div className="text-[10px] flex items-center gap-1.5" data-testid={`${testidPrefix}-status`}>
          {ICON[status]}
          {status === 'available' && <span className="text-emerald-600">Available</span>}
          {status === 'taken' && <span className="text-rose-500">Already exists</span>}
          {status === 'checking' && <span className="text-muted-foreground">Checking…</span>}
        </div>
      </div>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
        placeholder={placeholder}
        className={cn(
          'mt-1 h-9 font-mono-num',
          status === 'taken' && 'border-rose-500/60 focus-visible:ring-rose-500/40',
          status === 'available' && 'border-emerald-500/60 focus-visible:ring-emerald-500/40',
        )}
        data-testid={`${testidPrefix}-input`}
      />
      <div className="mt-1 flex items-center justify-between gap-2 min-h-[14px]">
        <div className="text-[10px] text-muted-foreground truncate">
          {norm && norm !== (value || '') && (
            <>Will save as <span className="font-mono-num">{norm}</span></>
          )}
        </div>
        {status === 'taken' && suggestion && (
          <button
            type="button"
            onClick={() => onApplySuggestion?.(suggestion)}
            className="text-[10px] inline-flex items-center gap-1 text-accent hover:underline"
            data-testid={`${testidPrefix}-apply-suggestion`}
          >
            <Wand2 className="h-3 w-3" /> Use “{suggestion}”
          </button>
        )}
      </div>
    </div>
  );
};

export default SlugChecker;
